import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, Lock, Sparkles, LogIn, RefreshCw } from "lucide-react";
import { useStudioAccess } from "../hooks/useStudioAccess";

/**
 * Wraps any member-only tool route. Resolution logic:
 *   - Loading          → spinner card
 *   - Unauthenticated  → redirect to /studio/login?next=<current path>
 *   - Token invalid    → redirect to /studio/login?next=<current path>
 *   - Authenticated but no access → paywall view tailored to the reason:
 *       * deactivated          → "Account deactivated — contact Paul"
 *       * inactive_subscription→ "Reactivate subscription" CTA
 *   - Has access       → render children
 *
 * Props:
 *   children: the gated tool/page
 *   toolName: optional display name used in the paywall headline ("Chords Tool")
 *   description: short value-prop shown on paywall
 *   previewImage: optional /url to a preview screenshot. If omitted, a tasteful
 *                 dark gradient block stands in.
 */
export default function RequireStudioAccess({
  children,
  toolName = "this tool",
  description = "Available to active Studio members.",
  previewImage = null,
}) {
  const { loading, hasAccess, reason } = useStudioAccess();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b0f1e] text-white">
        <div className="flex items-center gap-3 text-white/60 text-sm">
          <Loader2 className="w-5 h-5 animate-spin" />
          Checking your access…
        </div>
      </div>
    );
  }

  // Unauthenticated / stale token → bounce to login w/ a redirect-back hint.
  if (reason === "unauthenticated" || reason === "token_invalid") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/studio/login?next=${next}`} replace />;
  }

  // Account deactivated by admin → can't recover via self-serve.
  if (reason === "deactivated") {
    return (
      <PaywallShell toolName={toolName} previewImage={previewImage}>
        <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
          Your account is paused
        </h1>
        <p className="text-white/65 mb-7 leading-relaxed">
          Access to the {toolName} is currently disabled. Reach out to Paul directly and we&apos;ll get you back in.
        </p>
        <a
          href="mailto:paul@paulvilas.com"
          data-testid="paywall-contact-admin"
          className="inline-flex items-center gap-2 bg-paul-yellow text-navy font-bold px-6 py-3 rounded-lg hover:brightness-105 transition"
          style={{ color: "#0b1535" }}
        >
          <LogIn className="w-4 h-4" />
          Contact Paul
        </a>
      </PaywallShell>
    );
  }

  // Subscriber with no active subscription (canceled / past_due) — soft paywall.
  if (reason === "inactive_subscription") {
    return (
      <PaywallShell toolName={toolName} previewImage={previewImage}>
        <div className="flex items-center gap-2 text-paul-yellow text-xs font-bold uppercase tracking-[0.18em] mb-3">
          <Sparkles className="w-4 h-4" />
          Studio members only
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-3 leading-[1.1]">
          Reactivate to unlock the {toolName}
        </h1>
        <p className="text-white/65 mb-7 leading-relaxed">
          {description} Your subscription is currently inactive — reactivate from your profile to regain access.
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href="/studio/profile"
            data-testid="paywall-reactivate-cta"
            className="inline-flex items-center gap-2 bg-paul-yellow text-navy font-bold px-6 py-3 rounded-lg hover:brightness-105 transition"
            style={{ color: "#0b1535" }}
          >
            <RefreshCw className="w-4 h-4" />
            Reactivate subscription
          </a>
          <a
            href="/studio"
            data-testid="paywall-back-to-studio"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-paul-yellow/40 hover:text-paul-yellow text-white/80 font-semibold px-6 py-3 rounded-lg transition"
          >
            Back to Studio
          </a>
        </div>
      </PaywallShell>
    );
  }

  // hasAccess === true → render the gated content.
  return children;
}

/**
 * Shared paywall page chrome — preview thumbnail on the left, copy/CTAs on the
 * right. Reused for every "no-access" sub-state so the visual language is
 * consistent across future tools.
 */
function PaywallShell({ toolName, previewImage, children }) {
  return (
    <div className="min-h-screen bg-[#0b0f1e] text-white">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <a
          href="/studio"
          className="inline-block text-xs uppercase tracking-[0.3em] text-white/40 hover:text-paul-yellow mb-10"
        >
          ← Studio
        </a>
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div className="order-2 md:order-1">{children}</div>
          <div className="order-1 md:order-2">
            {previewImage ? (
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40">
                <img
                  src={previewImage}
                  alt={`${toolName} preview`}
                  className="w-full h-auto block"
                />
              </div>
            ) : (
              <div className="aspect-[4/3] rounded-2xl border border-white/10 bg-gradient-to-br from-[#161a2c] via-[#10162a] to-[#0b0f1e] flex items-center justify-center shadow-2xl shadow-black/40">
                <Lock className="w-16 h-16 text-paul-yellow/30" strokeWidth={1.5} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
